import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";

export default function Header() {
  const { user, logout, promptLogin } = useAuth();
  const { lang, setLanguage, t } = useI18n();
  const navigate = useNavigate();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Consent (CGU/RGPD) is collected via the modal in LoginConsentModal before redirect.
    promptLogin();
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t("logout_confirm"));
    navigate("/");
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-[#F4F4F0] border-b-2 border-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center gap-4">
        <button
          onClick={() => navigate("/")}
          data-testid="brand-logo-btn"
          className="font-display text-lg md:text-xl uppercase tracking-tight text-[#0A0A0A] flex items-center gap-2"
        >
          <span className="inline-block w-3 h-3 bg-[#FF3B30] border-2 border-[#0A0A0A]" />
          {t("brand")}
        </button>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Language toggle */}
          <div className="flex items-center border-2 border-[#0A0A0A]" data-testid="lang-toggle">
            <button
              data-testid="lang-fr-btn"
              onClick={() => setLanguage("fr")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-colors ${
                lang === "fr"
                  ? "bg-[#0A0A0A] text-[#F4F4F0]"
                  : "bg-transparent text-[#0A0A0A] hover:bg-[#FFCC00]"
              }`}
            >
              FR
            </button>
            <button
              data-testid="lang-en-btn"
              onClick={() => setLanguage("en")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-colors border-l-2 border-[#0A0A0A] ${
                lang === "en"
                  ? "bg-[#0A0A0A] text-[#F4F4F0]"
                  : "bg-transparent text-[#0A0A0A] hover:bg-[#FFCC00]"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-nl-btn"
              onClick={() => setLanguage("nl")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-colors border-l-2 border-[#0A0A0A] ${
                lang === "nl"
                  ? "bg-[#0A0A0A] text-[#F4F4F0]"
                  : "bg-transparent text-[#0A0A0A] hover:bg-[#FFCC00]"
              }`}
            >
              NL
            </button>
          </div>

          {user ? (
            <div className="flex items-center gap-3">
              {user.is_admin && (
                <button
                  data-testid="nav-admin-btn"
                  onClick={() => navigate("/admin")}
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-3 py-2 shadow-brutal-sm hover:bg-[#FFCC00] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
                >
                  {t("nav_admin")}
                </button>
              )}
              {user.plan === "pro" && (
                <span className="hidden sm:inline-block bg-[#FFCC00] border-2 border-[#0A0A0A] px-2 py-1 text-[10px] font-bold uppercase tracking-widest">
                  {t("pro_badge")}
                </span>
              )}
              <div
                data-testid="user-avatar"
                className="hidden sm:flex items-center gap-2 border-2 border-[#0A0A0A] px-2 py-1 bg-white"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-6 h-6 object-cover"
                  />
                ) : (
                  <div className="w-6 h-6 bg-[#FFCC00] border border-[#0A0A0A]" />
                )}
                <span className="text-xs font-bold uppercase truncate max-w-[140px]">
                  {user.name}
                </span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-4 py-2 shadow-brutal-sm hover:bg-[#FFCC00] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all"
              >
                {t("nav_logout")}
              </button>
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={handleLogin}
              className="bg-[#FF3B30] text-white border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-4 py-2 shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all"
            >
              {t("nav_login")}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
